import React, { useState } from 'react';
import Button from '../ui/Button';
import Icon from '../AppIcon';
import PlanComparisonModal from './PlanComparisonModal';

const PlanSelectionStep = ({ onNext, onBack, onSaveData, initialData = {} }) => {
  const [selectedPlan, setSelectedPlan] = useState(initialData.selectedPlan || null);
  const [showComparison, setShowComparison] = useState(false);

  const plans = {
    free: {
      id: 'free',
      name: 'Free Plan',
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: [
        '2 AI reviews per month',
        'Full access to all features',
        'Email & SMS review requests',
        'Analytics dashboard',
        'External platform integration',
        'Thank you page customization'
      ],
      description: 'Perfect for getting started with AI-powered reviews',
      highlight: false
    },
    pro: {
      id: 'pro',
      name: 'Pro Plan',
      monthlyPrice: 97,
      yearlyPrice: 597,
      features: [
        'Unlimited AI reviews',
        'All Free plan features',
        'Priority support',
        'Custom integrations'
      ],
      description: 'Scale your review management without limits',
      highlight: true
    }
  };

  const handlePlanSelect = (planId) => {
    const plan = plans[planId];
    setSelectedPlan(plan);
    
    // Save the plan selection data
    onSaveData({
      planSelection: {
        selectedPlan: plan,
        selectionTimestamp: new Date().toISOString()
      }
    });
  };

  const handleContinue = () => {
    if (selectedPlan) {
      onNext();
    }
  };

  const PlanCard = ({ plan, isSelected, onSelect }) => (
    <div 
      className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
        isSelected 
          ? 'border-primary bg-primary/5 shadow-lg' 
          : 'border-border hover:border-primary/50 bg-surface'
      } ${plan.highlight ? 'ring-2 ring-primary/20' : ''}`}
      onClick={() => onSelect(plan.id)}
    >
      {plan.highlight && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
            Most Popular
          </span>
        </div>
      )}
      
      <div className="text-center mb-4">
        <h3 className="text-xl font-bold text-text-primary mb-2">{plan.name}</h3>
        <div className="mb-2">
          <span className="text-3xl font-bold text-text-primary">
            ${plan.monthlyPrice}
          </span>
          <span className="text-text-secondary">/month</span>
        </div>
        {plan.yearlyPrice > 0 && (
          <div className="text-sm text-text-secondary">
            or ${plan.yearlyPrice}/year (save ${(plan.monthlyPrice * 12) - plan.yearlyPrice})
          </div>
        )}
        <p className="text-text-secondary text-sm mt-2">{plan.description}</p>
      </div>

      <ul className="space-y-2 mb-6">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-center text-sm text-text-secondary">
            <Icon name="Check" size={16} className="text-primary mr-2 flex-shrink-0" />
            {feature}
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-center">
        {isSelected ? (
          <div className="flex items-center text-primary font-medium">
            <Icon name="CheckCircle" size={20} className="mr-2" />
            Selected
          </div>
        ) : (
          <span className="text-text-muted">Click to select</span>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CreditCard" size={32} className="text-primary" />
        </div>
        <h2 className="text-3xl font-bold text-text-primary mb-2">Choose Your Plan</h2>
        <p className="text-text-secondary text-lg max-w-2xl mx-auto">
          Start with our Free plan or unlock unlimited AI reviews with Pro. 
          You can upgrade or downgrade anytime.
        </p>
      </div>

      {/* Plan Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <PlanCard 
          plan={plans.free} 
          isSelected={selectedPlan?.id === 'free'}
          onSelect={handlePlanSelect}
        />
        <PlanCard 
          plan={plans.pro} 
          isSelected={selectedPlan?.id === 'pro'}
          onSelect={handlePlanSelect}
        />
      </div>

      {/* Comparison Link */}
      <div className="text-center mb-8">
        <button 
          onClick={() => setShowComparison(true)}
          className="text-primary hover:text-primary/80 underline text-sm font-medium"
        >
          See detailed plan comparison →
        </button>
      </div>

      {/* Pro Plan Benefits */}
      {selectedPlan?.id === 'pro' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <Icon name="Sparkles" size={20} className="text-green-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-green-800 mb-1">Great choice!</h4>
              <p className="text-green-700 text-sm">
                You'll have unlimited AI reviews and priority support.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center pt-6 border-t border-border">
        <Button 
          variant="outline" 
          onClick={onBack}
          className="flex items-center space-x-2"
        >
          <Icon name="ArrowLeft" size={16} />
          <span>Back</span>
        </Button>

        <Button 
          onClick={handleContinue}
          disabled={!selectedPlan}
          className="flex items-center space-x-2"
        >
          <span>Continue</span>
          <Icon name="ArrowRight" size={16} />
        </Button>
      </div>
    </div>

    {/* Plan Comparison Modal */}
    <PlanComparisonModal 
      isOpen={showComparison}
      onClose={() => setShowComparison(false)}
      onSelectPlan={handlePlanSelect}
    />
  </>
  );
};

export default PlanSelectionStep;